/**
 * 품질 이력 분석 페이지
 * - 기간·품목(차종/품명/품번)·센서데이터(범위) 필터로 날짜별 생산/NG 통계 차트 표시
 * - statsDatas API 호출 후 BarChart로 OK/NG 스택 막대 시각화
 */
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { components } from 'react-select';
import { useOutletContext } from 'react-router';
import moment from 'moment';
import axios from 'axios';
import "react-datepicker/dist/react-datepicker.css";
import { CustomDatePickerStart, CustomDatePickerEnd } from '../../components/CustomDatePicker';
import Select from 'react-select';
import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis, Legend, LabelList, ReferenceLine, Tooltip } from 'recharts';
import resetIconImg from '../../../images/resetIcon.png';
import filledArrowIconImg from '../../../images/filledArrowIcon.png';
import { API_URL } from '../../../config/constants';
import NoData from '../../components/NoData';
import './sum_data.scss';
import { getModelTypes, getModelNames, getModelNos, getSensorLabels } from '../../../api/modelApi';

/* ---- 차트 시각화 상수 ---- */
const CHART_HEIGHT = 350;
const BAR_WIDTH_PER_ITEM_PX = 56;           // 날짜 1개당 막대 너비 (가로 스크롤 시 고정폭)
const CHART_MIN_WIDTH_PX = 400;             // 차트 최소 너비
const MIN_BAR_HEIGHT_FOR_INSIDE_LABEL_PX = 28; // 이 높이 미만이면 라벨을 막대 바깥에 표시
const COUNT_LABEL_OUTSIDE_OFFSET_PX = 35;    // 총생산 라벨 위쪽 오프셋
const NG_LABEL_ABOVE_TOTAL_PX = 62;         // 총 생산이 차트 밖에 있을 때 NG가 총 생산 위에 오도록 (총생산 17px + OK막대 여유)
const NG_BAR_VERY_THIN_PX = 6;             // 이 미만이면 총 생산이 막대 안에 있는 걸로 간주 (NG만 막대 바로 위)
const XAXIS_TICK_TARGET = 12;               // X축 tick 개수 목표 (날짜 많을 때 간격 조절)
const XAXIS_TICK_FONT_SIZE = 12;
const BAR_CATEGORY_GAP_PERCENT = 8;         // 막대 군 간격 (%)
const BAR_GAP_PERCENT = 4;                  // OK/NG 막대 간 간격 (%)

const OPTION_ALL = { value: '', label: '전체 선택' };

/** Recharts BarChart 라벨: 파란(OK) 막대 안에 '총 생산' + 개수 표시, 막대가 작으면 위쪽에 표시 */
function CountBarLabel({ x, y, width, height, value }) {
    const count = Number(value) || 0;
    if (count <= 0) return null;
    const showInside = height >= MIN_BAR_HEIGHT_FOR_INSIDE_LABEL_PX;
    const yPos = showInside ? y + height / 2 : y - COUNT_LABEL_OUTSIDE_OFFSET_PX;
    const labelFill = showInside ? '#FFF' : '#0A2B83';
    return (
        <text
            x={x + width / 2}
            y={yPos}
            textAnchor="middle"
            fill={labelFill}
            fontSize={12}
            fontFamily="LeferiPointWhite"
        >
            <tspan x={x + width / 2} dy={showInside ? -2 : -2}>
                총 생산
            </tspan>
            <tspan x={x + width / 2} dy={14}>
                {count.toLocaleString()}
            </tspan>
        </text>
    );
}

/** Recharts BarChart 라벨: 주황(NG) 막대 위에 'NG {개수}' 표시 */
function NgBarLabel({ x, y, width, height, value }) {
    const ng = Number(value) || 0;
    if (ng <= 0) return null;
    // 총 생산이 막대 안: NG 막대가 아주 얇을 때만(6px 미만) 막대 바로 위. 그 외(총 생산이 차트 밖)는 NG를 총 생산 위에 두기 위해 오프셋 크게
    const totalInsideBar = height < NG_BAR_VERY_THIN_PX;
    const offsetPx = totalInsideBar ? 6 : NG_LABEL_ABOVE_TOTAL_PX;
    const yPos = y - offsetPx;
    return (
        <text
            x={x + width / 2}
            y={yPos}
            textAnchor="middle"
            fill="#E89B5C"
            fontSize={12}
            fontFamily="LeferiPointWhite"
        >
            {`NG ${ng.toLocaleString()}`}
        </text>
    );
}

function SumDataStatistic() {
    const { selSumDsId } = useOutletContext() || {};
    const processDisplayName = 'summ_data';

    /* ---- 기간·품목 필터 state ---- */
    /* 오늘 기준 최근 3일(오늘 포함) 기본값, 종료일은 접속 시각 */
    const [startDate, setStartDate] = useState(moment().subtract(2, 'days').startOf('day').toDate());
    const [endDate, setEndDate] = useState(moment().toDate());
    const [modelTypes, setModelTypes] = useState([]);           // 차종 목록 (API)
    const [modelNames, setModelNames] = useState([]);           // 품명 목록 (API)
    const [modelNos, setModelNos] = useState([]);               // 품번 목록 (API)
    const [selectedModelType, setSelectedModelType] = useState(OPTION_ALL);
    const [selectedModelName, setSelectedModelName] = useState(OPTION_ALL);
    const [selectedModelNo, setSelectedModelNo] = useState(OPTION_ALL);
    const [sensor, setSensor] = useState([]);                   // 센서데이터(범위) 목록 (getSensorLabels)
    const [selectedSensor, setSelectedSensor] = useState({ value: 'all', label: '전체 선택' }); // 선택한 센서 → scope로 변환해 API 전달
    const [selectPeriod, setSelectPeriod] = useState('currentWorks'); // 'input' | 'currentWorks' | '1h' | '24h' (데이터관리와 동일)
    const [scope, setScope] = useState("all");                  // all | 원자재 | 하중 | 치수 | dec0~ (백엔드 sDecision 필터용)

    /* ---- 차트 데이터 state ---- */
    const [total, setTotal] = useState([]);                     // statsDatas API 응답 dateData
    const [isLoadingChart, setIsLoadingChart] = useState(false);

    /** statsDatas API 호출 → 날짜별 count/ngCount를 total state에 저장. 차종=전체면 날짜만, 차종 선택 시 해당 차종(+ 품명/품번 선택 시) 필터 */
    const fetchBarData = useCallback(async () => {
        if (!selSumDsId || selSumDsId < 0) {
            setTotal([]);
            return;
        }
        const formatDate = (date) => {
            if (!date) return moment().format('YYYY-MM-DD');
            const d = moment(date);
            if (!d.isValid()) return moment().format('YYYY-MM-DD');
            return d.format('YYYY-MM-DD');
        };
        const modelTypeVal = selectedModelType != null && typeof selectedModelType === 'object' && 'value' in selectedModelType ? selectedModelType.value : selectedModelType;
        const modelNameVal = selectedModelName != null && typeof selectedModelName === 'object' && 'value' in selectedModelName ? selectedModelName.value : selectedModelName;
        const modelNoVal = selectedModelNo != null && typeof selectedModelNo === 'object' && 'value' in selectedModelNo ? selectedModelNo.value : selectedModelNo;

        const requestBody = {
            sumDsId: selSumDsId,
            startDate: formatDate(startDate),
            endDate: formatDate(endDate),
            scope: scope,
        };
        // 전체가 아닐 때만 해당 필터 추가 (차종=전체면 날짜만, 품명=전체면 차종+날짜, 품번=전체면 차종+품명+날짜)
        if (modelTypeVal != null && String(modelTypeVal).trim() !== '') requestBody.modelType = String(modelTypeVal).trim();
        if (modelNameVal != null && String(modelNameVal).trim() !== '') requestBody.modelName = String(modelNameVal).trim();
        if (modelNoVal != null && String(modelNoVal).trim() !== '') requestBody.modelNo = String(modelNoVal).trim();

        setTotal([]);
        setIsLoadingChart(true);

        try {
            console.log('[차트] requestBody', requestBody);
            const response = await axios.post(`${API_URL}/android/statsDatas`, requestBody, {
                withCredentials: true,
            });

            let data = response?.data;
            console.log('[resp] scope=', scope, 'data=', data);
            console.log('[resp] scope=', scope, 'first dateData=', data?.dateData?.[0]);
            console.log('[resp] scope=', scope, 'sum ng=', (data?.dateData ?? []).reduce((s,r)=>s+Number(r.ngCount||0),0));
            if (data == null) {
                setTotal([]);
                return;
            }
            if (typeof data === 'string') data = JSON.parse(data);
            const rawDateData = data?.dateData ?? [];
            const mappedData = rawDateData.map((r) => {
                let dateVal = r.date;
                if (dateVal && typeof dateVal === 'string' && dateVal.indexOf('T') !== -1) {
                    dateVal = dateVal.slice(0, 10);
                }
                const count = Number(r.count) || 0;
                const ngCount = Number(r.ngCount) || 0;
                const okCount = Math.max(0, count - ngCount);
                return {
                    date: dateVal,
                    count,
                    ngCount,
                    okCount,
                };
            });
            setTotal([...mappedData]);
        } catch (err) {
            console.error('[차트] statsDatas API error:', err);
            setTotal([]);
        } finally {
            setIsLoadingChart(false);
        }
    }, [selSumDsId, startDate, endDate, selectedModelType, selectedModelName, selectedModelNo, scope]);

    /** 기간 옵션 (데이터관리 페이지와 동일) */
    const periodOpt = [
        { label: '시간직접입력', value: 'input' },
        { label: '현재공정', value: 'currentWorks' },
        { label: '최근 1시간', value: '1h' },
        { label: '최근 24시간', value: '24h' },
    ];

    /** selectPeriod 변경 시 startDate/endDate 동기화 (데이터관리와 동일 로직) */
    useEffect(() => {
        switch (selectPeriod) {
            case 'input':
                break;
            case 'currentWorks':
                setStartDate(moment().subtract(2, 'days').startOf('day').toDate());
                setEndDate(moment().toDate());
                break;
            case '1h':
                setStartDate(moment().subtract(1, 'hours').toDate());
                setEndDate(moment().toDate());
                break;
            case '24h':
                setStartDate(moment().subtract(1, 'days').toDate());
                setEndDate(moment().toDate());
                break;
            default:
                setStartDate(moment().toDate());
                setEndDate(moment().toDate());
                break;
        }
    }, [selectPeriod]);

    useEffect(() => {
        fetchBarData();
    }, [fetchBarData]);

    /** 공정 선택 시 차종 목록 로드 */
    useEffect(() => {
        if (!selSumDsId || selSumDsId < 0) return;
        getModelTypes({ sumDsId: selSumDsId })
          .then((data) => {
            const opts = Array.isArray(data) ? data.map((el) => (typeof el === 'string' ? { value: el, label: el } : el)) : [];
            setModelTypes(opts);
          })
          .catch(() => setModelTypes([]));
    }, [selSumDsId]);

    /** 차종 선택 시 품명 목록 로드 (차종=전체면 로드 안 함) */
    useEffect(() => {
        if (!selSumDsId || selSumDsId < 0 || !selectedModelType || selectedModelType.value === '') {
            setModelNames([]);
            return;
        }
        getModelNames({ sumDsId: selSumDsId, modelType: selectedModelType })
          .then((data) => {
            const opts = Array.isArray(data) ? data.map((el) => (typeof el === 'string' ? { value: el, label: el } : el)) : [];
            setModelNames(opts);
          })
          .catch(() => setModelNames([]));
    }, [selSumDsId, selectedModelType]);

    /** 품명 선택 시 품번 목록 로드 (품명=전체면 로드 안 함) */
    useEffect(() => {
        if (!selSumDsId || selSumDsId < 0 || !selectedModelName || selectedModelName.value === '') {
            setModelNos([]);
            return;
        }
        getModelNos({ sumDsId: selSumDsId, modelType: selectedModelType, modelName: selectedModelName })
          .then((data) => {
            const opts = Array.isArray(data) ? data.map((el) => (typeof el === 'string' ? { value: el, label: el } : el)) : [];
            setModelNos(opts);
          })
          .catch(() => setModelNos([]));
    }, [selSumDsId, selectedModelType, selectedModelName]);

    /** 공정 선택 시 센서데이터(범위) 목록 로드 → scope 필터 옵션 제공 */
    useEffect(() => {
        if (!selSumDsId) return;
        getSensorLabels({ sumDsId: selSumDsId })
          .then((data) => {
            console.log('data', data);
            const opts = Array.isArray(data)
              ? data.map((el) => ({
                  value: el.key,
                  label: el.label,
                }))
              : [];
            console.log('opts', opts);
            setSensor(opts);
          })
          .catch((e) => {
            console.error("getSensorLabels failed:", e);
            setSensor([]);
          });
    }, [selSumDsId]);

    /** total을 선택 기간(startDate~endDate)으로 필터 → 차트·요약에 사용 */
    const chartData = useMemo(() => {
        const start = moment(startDate).startOf('day');
        const end = moment(endDate).endOf('day');
        const formats = ['YYYY-MM-DD', 'YY.MM.DD', 'YYYY.MM.DD', 'YYYY/MM/DD', moment.ISO_8601];
      
        const filtered = (total || []).filter((d) => {
          let dateStr = d.date;
          if (dateStr instanceof Date) {
            dateStr = moment(dateStr).format('YYYY-MM-DD');
          } else if (typeof dateStr === 'string' && dateStr.indexOf('T') !== -1) {
            dateStr = dateStr.slice(0, 10);
          } else if (typeof dateStr !== 'string') {
            dateStr = String(dateStr);
          }
          let day = moment(dateStr, formats, true);
          if (!day.isValid()) day = moment(dateStr);
          if (!day.isValid()) return false;
          return day.isSameOrAfter(start) && day.isSameOrBefore(end);
        });
        return filtered.map((d) => {
          let dateStr = d.date;
          if (typeof dateStr === 'string' && dateStr.indexOf('T') !== -1) dateStr = dateStr.slice(0, 10);
          else if (dateStr instanceof Date) dateStr = moment(dateStr).format('YYYY-MM-DD');
          else if (typeof dateStr !== 'string') dateStr = String(dateStr);
          return { ...d, date: dateStr };
        });
      }, [total, startDate, endDate]);
      

    /** 차종/품명/품번 변경 시 BarChart key로 리마운트 (Recharts 렌더 캐시 방지) */
    const chartDataKey = useMemo(
        () =>
            [selectedModelType?.value, selectedModelName?.value, selectedModelNo?.value]
                .filter(Boolean)
                .join('_') || 'all',
        [selectedModelType, selectedModelName, selectedModelNo]
    );

    useEffect(() => {
        console.log('[차트] chartData 최종값', { length: (chartData || []).length, key: chartDataKey });
    }, [chartData, chartDataKey]);

    /** chartData 기준 요약 통계 (총 생산, 총 NG, 평균 NG율, 최다 NG일 등) */
    const summaryStats = useMemo(() => {
        const totalProduction = (chartData || []).reduce((sum, d) => sum + (Number(d.count) || 0), 0);
        const totalNG = (chartData || []).reduce((sum, d) => sum + (Number(d.ngCount) || 0), 0);
        const avgNGRate =
            totalProduction > 0
                ? ((totalNG / totalProduction) * 100).toFixed(2) + '%'
                : '0%';
        const withRate = (chartData || [])
            .filter((d) => (Number(d.count) || 0) > 0)
            .map((d) => {
                const t = Number(d.count) || 0;
                const n = Number(d.ngCount) || 0;
                return { date: d.date, rate: t ? (n / t) * 100 : 0 };
            });
        const maxRateRow = withRate.length
            ? withRate.reduce((a, b) => (a.rate >= b.rate ? a : b), { date: '', rate: 0 })
            : null;
        const maxNGRow = (chartData || []).reduce(
            (best, d) => {
                const n = Number(d.ngCount) || 0;
                return !best || n > (Number(best.ngCount) || 0) ? d : best;
            },
            null
        );
        return {
            totalProduction,
            totalNG,
            avgNGRate,
            selectedType: selectedModelType?.label ?? '-',
            selectedName: selectedModelName?.label ?? '-',
            selectedNo: selectedModelNo?.label ?? '-',
            selectedSensor: selectedSensor?.label ?? '-',
            maxNGDate: maxNGRow?.date ?? '-',
            maxNGRate: maxRateRow ? `${maxRateRow.rate.toFixed(2)}%` : '-',
        };
    }, [chartData, selectedModelType, selectedModelName, selectedModelNo, selectedSensor]);

    /** 선택 기간의 일수 (시작일~종료일 양 끝 포함) */
    const selectedDaysCount = useMemo(() => {
        const start = moment(startDate).startOf('day');
        const end = moment(endDate).startOf('day');
        const days = end.diff(start, 'days') + 1;
        return Math.max(0, days);
    }, [startDate, endDate]);

    /** scope 필터 옵션 (전체 / 원자재 / 하중 / 치수) - 센서데이터 드롭다운에서 sensorOptions 사용 시 scope로 변환 */

    /** 차트 Y축 최대값·점선 보조선 (데이터 max에 맞춰 자동 계산) */
    const chartScale = useMemo(() => {
        const maxVal = !chartData?.length
            ? 0
            : Math.max(...chartData.map((d)=>Number(d.count)||0));
        const niceSteps = [1000, 2000, 5000, 10000, 20000, 50000, 100000, 200000, 500000];
        const step = niceSteps.find((s) => s * 5 >= maxVal) || niceSteps[niceSteps.length - 1];
        let domainMax = step * Math.ceil(maxVal / step);
        if (maxVal > 0 && domainMax < maxVal) domainMax += step;
        if (domainMax <= 0) domainMax = 5000;
        const refLines = [];
        for (let y = step; y <= domainMax; y += step) refLines.push(y);
        return { domainMax, refLines };
    }, [chartData]);

    /** 날짜 개수에 따른 차트 너비·X축 tick interval (과밀 방지, 가로 스크롤) */
    const chartLayout = useMemo(() => {
        const len = (chartData || []).length;
        const width = Math.max(len * BAR_WIDTH_PER_ITEM_PX, CHART_MIN_WIDTH_PX);
        const interval = len <= XAXIS_TICK_TARGET ? 0 : Math.max(0, Math.floor(len / XAXIS_TICK_TARGET) - 1);
        return { chartWidth: width, xAxisInterval: interval };
    }, [chartData]);

    /** 적용 버튼: 필터 반영 후 차트 데이터 재조회 */
    const handleApply = (e) => {
        e?.preventDefault?.();
        // fetchBarData();
    };

    /** 기간 초기화 버튼: 현재공정(오늘 기준 최근 3일, 종료일=현재 시각)으로 설정 */
    const handleResetPeriod = (e) => {
        e?.preventDefault?.();
        setSelectPeriod('currentWorks');
        setStartDate(moment().subtract(2, 'days').startOf('day').toDate());
        setEndDate(moment().toDate());
    };

    /* ---- react-select 옵션: 전체 + API 데이터 ---- */
    const modelTypeOptions = useMemo(() => [OPTION_ALL, ...modelTypes.map((t) => (typeof t === 'string' ? { value: t, label: t } : t))], [OPTION_ALL, modelTypes]);
    const modelNameOptions = useMemo(() => {
        if (!selectedModelType || selectedModelType.value === '') return [OPTION_ALL];
        return [OPTION_ALL, ...modelNames.map((n) => (typeof n === 'string' ? { value: n, label: n } : n))];
    }, [OPTION_ALL, modelNames, selectedModelType]);
    const modelNoOptions = useMemo(() => {
        if (!selectedModelName || selectedModelName.value === '') return [OPTION_ALL];
        return [OPTION_ALL, ...modelNos.map((no) => (typeof no === 'string' ? { value: no, label: no } : no))];
    }, [OPTION_ALL, modelNos, selectedModelName]);
    const sensorOptions = useMemo(() => {
        const base = [{ value: "all", label: "전체 선택" }];
        return base.concat(sensor);
    }, [sensor]);

    if (selSumDsId == null || selSumDsId < 0) {
        return <NoData />;
    }

    return (
        <div className="sumDataStatistic">
            <p className="path">데이터 분석<span className="below">{'>'}</span>품질 이력 분석</p>
            <h2 className="pageTitle">품질 이력 분석 - {processDisplayName}</h2>

            {/* 필터 카드: 기간, 공정, 품목(차종/품명/품번), 센서데이터(범위) */}
            <div className="filterCard">
                <form onSubmit={handleApply}>
                    <div className="filterRow filterRow1">
                        <span className="datePickerLabel">기간</span>
                        <div className="datePickerArea">
                            <div className="startDateArea">
                                <CustomDatePickerStart
                                    startDate={startDate}
                                    setStartDate={setStartDate}
                                    endDate={endDate}
                                    changeDropdown={() => setSelectPeriod('input')}
                                />
                            </div>
                            <span>-</span>
                            <div className="endDateArea">
                                <CustomDatePickerEnd
                                    startDate={startDate}
                                    endDate={endDate}
                                    setEndDate={setEndDate}
                                    changeDropdown={() => setSelectPeriod('input')}
                                />
                            </div>
                        </div>
                        <Select
                            options={periodOpt}
                            value={periodOpt.find((el) => el.value === selectPeriod)}
                            onChange={(sel) => setSelectPeriod(sel.value)}
                            isSearchable={false}
                            className="filterSelect periodSelect periodOnly"
                            classNames={{
                                control: (state) => (state.isFocused ? 'selectControl focused' : 'selectControl'),
                                container: () => 'selectBox',
                                menu: () => 'selectMenu',
                                menuList: () => 'optionsList',
                                option: (s) => (s.isSelected ? 'option selected' : s.isFocused ? 'option focused' : 'option'),
                                singleValue: () => 'singleVal',
                                valueContainer: () => 'valueContainer',
                              }}
                            components={{
                                DropdownIndicator: (props) => (
                                  <components.DropdownIndicator {...props}>
                                    <span className={`arrowIcon ${props.isFocused ? 'focused' : ''}`}>
                                      <img src={filledArrowIconImg} alt="드롭다운" />
                                    </span>
                                  </components.DropdownIndicator>
                                ),
                                IndicatorSeparator: () => null,
                              }}
                        />
                        <button type="submit" className="applyBtn">적용</button>
                        <button type="button" className="resetPeriodBtn" onClick={handleResetPeriod}>
                            <span>기간 초기화</span>
                            <img src={resetIconImg} alt="초기화" />
                        </button>
                    </div>
                    <div className="filterRow filterRow2">
                        <span className="filterLabel">품목</span>
                        <div className="selectGroup">
                            <div className="selectItem">
                                <span className="selectItemLabel">차종</span>
                                <Select
                                    options={modelTypeOptions}
                                    value={selectedModelType}
                                    onChange={(v) => {
                                        console.log('[차트] 차종 변경', v?.value ?? v);
                                        setSelectedModelType(v);
                                        setSelectedModelName(null);
                                        setSelectedModelNo(null);
                                    }}
                                    placeholder="전체 선택"
                                    isSearchable={false}
                                    classNames={{
                                        control: () => 'selectControl',
                                        container: () => 'selectBox',
                                        menu: () => 'selectMenu',
                                        option: (s) => s.isSelected ? 'option selected' : s.isFocused ? 'option focused' : 'option',
                                        singleValue: () => 'singleVal',
                                        valueContainer: () => 'valueContainer',
                                        placeholder: () => 'selectPlaceholder',
                                    }}
                                    components={{
                                        DropdownIndicator: (s) => (
                                            <span className={s.isFocused ? 'arrowIcon focused' : 'arrowIcon'}>
                                                <img src={filledArrowIconImg} alt="드롭다운" />
                                            </span>
                                        ),
                                        IndicatorSeparator: () => null,
                                    }}
                                />
                            </div>
                            <div className="selectItem">
                                <span className="selectItemLabel">품명</span>
                                <Select
                                    options={modelNameOptions}
                                    value={selectedModelName}
                                    onChange={(v) => {
                                        setSelectedModelName(v);
                                        setSelectedModelNo(null);
                                    }}
                                    placeholder="전체 선택"
                                    isSearchable={false}
                                    isDisabled={!selectedModelType || selectedModelType.value === ''}
                                    classNames={{
                                        control: (state) => state.isDisabled ? 'selectControl selectControl--disabled' : 'selectControl',
                                        container: () => 'selectBox',
                                        menu: () => 'selectMenu',
                                        option: (s) => s.isSelected ? 'option selected' : s.isFocused ? 'option focused' : 'option',
                                        singleValue: () => 'singleVal',
                                        valueContainer: () => 'valueContainer',
                                        placeholder: () => 'selectPlaceholder',
                                    }}
                                    components={{
                                        DropdownIndicator: (props) => (
                                            <components.DropdownIndicator {...props}>
                                                <span className={`arrowIcon ${props.isFocused ? 'focused' : ''}`}>
                                                    <img src={filledArrowIconImg} alt="드롭다운" />
                                                </span>
                                            </components.DropdownIndicator>
                                        ),
                                        IndicatorSeparator: () => null,
                                    }}
                                />
                            </div>
                            <div className="selectItem">
                                <span className="selectItemLabel">품번</span>
                                <Select
                                    options={modelNoOptions}
                                    value={selectedModelNo}
                                    onChange={(v) => setSelectedModelNo(v)}
                                    placeholder="전체 선택"
                                    isSearchable={false}
                                    isDisabled={!selectedModelName || selectedModelName.value === ''}
                                    classNames={{
                                        control: (state) => state.isDisabled ? 'selectControl selectControl--disabled' : 'selectControl',
                                        container: () => 'selectBox',
                                        menu: () => 'selectMenu',
                                        menuList: () => 'optionsList',
                                        option: (s) => s.isSelected ? 'option selected' : s.isFocused ? 'option focused' : 'option',
                                        singleValue: () => 'singleVal',
                                        valueContainer: () => 'valueContainer',
                                        placeholder: () => 'selectPlaceholder',
                                    }}
                                    components={{
                                        DropdownIndicator: (props) => (
                                            <components.DropdownIndicator {...props}>
                                                <span className={`arrowIcon ${props.isFocused ? 'focused' : ''}`}>
                                                    <img src={filledArrowIconImg} alt="드롭다운" />
                                                </span>
                                            </components.DropdownIndicator>
                                        ),
                                        IndicatorSeparator: () => null,
                                    }}
                                />
                            </div>
                            <div className="selectItem">
                                <span className="selectItemLabel">센서데이터</span>
                                <Select
                                    options={sensorOptions}
                                    value={selectedSensor}
                                    onChange={(v) => {
                                        setSelectedSensor(v);
                                        setScope(v?.value ?? 'decision');
                                    }}
                                    placeholder="전체 선택"
                                    isSearchable={false}
                                    classNames={{
                                        control: () => 'selectControl',
                                        container: () => 'selectBox',
                                        menu: () => 'selectMenu',
                                        menuList: () => 'optionsList',
                                        option: (s) => s.isSelected ? 'option selected' : s.isFocused ? 'option focused' : 'option',
                                        singleValue: () => 'singleVal',
                                        valueContainer: () => 'valueContainer',
                                        placeholder: () => 'selectPlaceholder',
                                    }}
                                    components={{
                                        DropdownIndicator: (props) => (
                                            <components.DropdownIndicator {...props}>
                                                <span className={`arrowIcon ${props.isFocused ? 'focused' : ''}`}>
                                                    <img src={filledArrowIconImg} alt="드롭다운" />
                                                </span>
                                            </components.DropdownIndicator>
                                        ),
                                        IndicatorSeparator: () => null,
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </form>
            </div>

            {/* 생산/NG 막대 차트 (가로 스크롤 지원) */}
            <div className="chartCard">
                <div className="chartTitle">생산 및 품질 결과 (개)</div>
                <div className="chartWrapper" style={{ overflowX: 'auto', overflowY: 'hidden' }}>
                    {isLoadingChart ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: CHART_HEIGHT, color: '#888', fontSize: '14px' }}>
                            로딩 중...
                        </div>
                    ) : chartData && chartData.length > 0 ? (
                        <div style={{ width: chartLayout.chartWidth, minWidth: '100%' }}>
                            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                                <BarChart
                                    key={chartDataKey}
                                    data={chartData}
                                    margin={{ top: 24, right: 30, left: 20, bottom: 48 }}
                                    barCategoryGap={`${BAR_CATEGORY_GAP_PERCENT}%`}
                                    barGap={BAR_GAP_PERCENT}
                                    barSize={Math.min(48, Math.max(24, BAR_WIDTH_PER_ITEM_PX - 12))}
                                >
                                    <XAxis
                                        dataKey="date"
                                        axisLine={{ stroke: '#ccc' }}
                                        interval={chartLayout.xAxisInterval}
                                        angle={-45}
                                        textAnchor="end"
                                        height={56}
                                        tick={{ fontFamily: 'LeferiPointWhite', fontSize: XAXIS_TICK_FONT_SIZE, fill: '#4D4D4D' }}
                                        tickFormatter={(val) => (val && typeof val === 'string' ? val.split('T')[0].replace(/-/g, '.') : val)}
                                        label={{ value: '일자', position: 'bottom', offset: 0, style: { fontFamily: 'LeferiPointWhite' } }}
                                    />
                                    <YAxis
                                        domain={[0, chartScale.domainMax]}
                                        axisLine={{ stroke: '#ccc' }}
                                        tick={{ fontFamily: 'LeferiPointWhite', fontSize: 12, fill: '#4D4D4D' }}
                                        tickFormatter={(v) => v >= 10000 ? `${v / 10000}만` : String(v)}
                                    />
                                    <Tooltip
                                        formatter={(value, name) => [typeof value === 'number' ? value.toLocaleString() : value, name === 'count' ? '총 생산 (개)' : name === 'ngCount' ? 'NG (개)' : name]}
                                        labelFormatter={(label) => `일자: ${label && typeof label === 'string' ? label.split('T')[0].replace(/-/g, '.') : label}`}
                                        contentStyle={{ fontFamily: 'LeferiPointWhite' }}
                                    />
                                    {chartScale.refLines.map((y) => (
                                        <ReferenceLine
                                            key={y}
                                            y={y}
                                            stroke="#d0d0d0"
                                            strokeDasharray="4 4"
                                            strokeWidth={1}
                                        />
                                    ))}
                                    <Legend
                                        wrapperStyle={{ paddingTop: 16 }}
                                        formatter={(value) => <span className="legendLabel">{value}</span>}
                                    />
                                    {/* 파란: OK, 주황: NG (스택 합 = 총 생산) */}
                                    <Bar
                                        dataKey="okCount"
                                        name="OK"
                                        stackId="a"
                                        fill="#0A2B83"
                                        radius={[0, 0, 0, 0]}
                                    >
                                         <LabelList dataKey="count" content={CountBarLabel} />
                                    </Bar>
                                    <Bar dataKey="ngCount" name="NG" stackId="a" fill="#E89B5C" radius={[0, 0, 0, 0]}>
                                        <LabelList dataKey="ngCount"
                                            content={NgBarLabel}
                                        />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: CHART_HEIGHT,
                            color: '#888',
                            fontSize: '14px',
                        }}>
                            {!selectedModelType ? '차종을 먼저 선택해 주세요.' : '선택한 기간에 데이터가 없습니다.'}
                        </div>
                    )}
                </div>
            </div>

            {/* 요약 통계 카드 (2행: 1행 선택/품목, 2행 생산/NG) */}
            <div className="summaryCard">
                <ul className="summaryList">
                    <li>총 선택 일자 : {selectedDaysCount}일</li>
                    <li>선택 차종 : {summaryStats.selectedType}</li>
                    <li>품명 : {summaryStats.selectedName}</li>
                    <li>품번 : {summaryStats.selectedNo}</li>
                    <li>선택 센서 : {summaryStats.selectedSensor}</li>
                    <li>총 생산 수량 : {summaryStats.totalProduction.toLocaleString()}개</li>
                    <li>총 NG 발생 건수 : {summaryStats.totalNG.toLocaleString()}개</li>
                    <li>평균 NG 발생률: {summaryStats.avgNGRate}</li>
                    <li>최다 NG 발생일 : {summaryStats.maxNGDate}</li>
                    <li>최고 NG 발생률: {summaryStats.maxNGRate}</li>
                </ul>
            </div>
        </div>
    );
}

export default SumDataStatistic;
